import { CalendarClock } from 'lucide-react';
import { useState } from 'react';

import { FilterPills } from '@/components/ui/filter-pills';
import { formatDuration } from '@/lib/dates';
import { DateOverridesTab } from '@/pages/admin/scheduling/DateOverridesTab';
import { WeeklyScheduleEditor } from '@/pages/admin/scheduling/WeeklyScheduleEditor';
import { useWeeklyDraft } from '@/pages/admin/scheduling/useWeeklyDraft';
import type { SchedulingTeamMember } from '@shared/schemas/availability';

/*
 * The right-hand pane of /admin/scheduling: one person, their header, and the
 * two tabs. ROADMAP.md §7.2.
 *
 * **Mounted with a `key` of the employee id**, which is what makes "select
 * somebody else" a clean slate: the draft, the open tab and the month being
 * looked at all reset because the component is a new one. Without that, an
 * unsaved Monday could be carried onto a different cleaner and saved there.
 *
 * The header's running total comes from `useWeeklyDraft`, the same hook the
 * editor's rows read, so `5 days · 42h 30m/week` tracks the *unsaved* form. That
 * is the point of the figure: `Mon–Fri 8–5` with a half-hour break is 42h30,
 * which is two and a half hours over the weekly overtime threshold
 * (business-rules.md §5), and a total that only updates after saving would show
 * that too late to be a decision.
 */

type Tab = 'weekly' | 'overrides';

const TABS = [
  { value: 'weekly' as const, label: 'Weekly Schedule' },
  { value: 'overrides' as const, label: 'Date Overrides' },
];

export function EmployeeSchedulePane({ member }: { member: SchedulingTeamMember }) {
  const [tab, setTab] = useState<Tab>('weekly');
  const draft = useWeeklyDraft(member.employeeId);

  // The saved figures until the week has loaded, so the header does not flash
  // `0 days · 0m` for somebody who works five.
  const totals = draft.query.isPending
    ? { daysWorking: member.daysWorking, minutes: member.weeklyMinutes }
    : draft.totals;

  return (
    // Named after the person, so it is a landmark a screen reader can jump to
    // and the *only* region carrying their weekly total — the same figure also
    // appears in the team list, and "which one is on screen" should not be a
    // question either a person or a test has to guess at.
    <section
      aria-labelledby="schedule-pane-heading"
      className="min-w-0 rounded-lg border border-ink-300 bg-white shadow-card"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-4 py-3">
        <div className="min-w-0">
          <h2 id="schedule-pane-heading" className="text-h3 text-ink-900">
            {member.name}
          </h2>
          <p className="text-meta text-ink-500">
            {member.positionName ?? 'No position set'} · up to {member.maxJobsPerDay} job
            {member.maxJobsPerDay === 1 ? '' : 's'} a day
          </p>
        </div>

        {/*
          The running weekly total, exactly as the prototype reads it:
          `5 days · 37h 30m/week`. `aria-live` because it changes as the form is
          edited rather than in response to a page load.
        */}
        <p
          aria-live="polite"
          className="flex items-center gap-1.5 rounded-full bg-honey-50 px-3 py-1.5 text-label text-ink-900"
        >
          <CalendarClock aria-hidden="true" className="size-4 text-bark-700" />
          {totals.daysWorking} day{totals.daysWorking === 1 ? '' : 's'} ·{' '}
          {formatDuration(totals.minutes)}/week
          {draft.dirty ? <span className="text-meta text-ink-500">(unsaved)</span> : null}
        </p>
      </header>

      <div className="border-b border-ink-100 px-4 py-3">
        <FilterPills
          label="Weekly schedule or date overrides"
          options={TABS}
          value={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'weekly' ? (
        <WeeklyScheduleEditor draft={draft} employeeName={member.name} />
      ) : (
        <DateOverridesTab employeeId={member.employeeId} employeeName={member.name} />
      )}
    </section>
  );
}
